import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API } from "@/context/AuthContext";
import {
  MapPin, Cpu, GitBranch, AlertTriangle, Users, Calendar,
  TrendingUp, Trophy, ExternalLink, ChevronRight, Maximize2,
  Minimize2, ArrowLeft, ClipboardList, Loader2
} from "lucide-react";
import ProjectMeetings from "@/components/projects/ProjectMeetings";
import { ProjectSprintsTab, ProjectMilestonesTab } from "@/components/projects/ProjectPlanningTabs";

// ── KPI Card ────────────────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, sub, color, bg }) {
  return (
    <div className={`${bg} rounded-xl p-4 border border-white/60`} data-testid={`ora-kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={15} className={color} />
        <p className="text-xs text-gray-500 font-medium">{label}</p>
      </div>
      <p className={`text-2xl font-bold font-['Outfit'] ${color}`}>{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

// ── Riesgo Card ──────────────────────────────────────────────────────────────
function RiesgoCard({ riesgo, idx }) {
  const [open, setOpen] = useState(false);
  const tipo = riesgo.tipo || "";
  const color = tipo === "Cuello de botella" ? "text-red-700 bg-red-100 border-red-200"
    : tipo === "Dependencia externa" ? "text-amber-700 bg-amber-100 border-amber-200"
    : "text-blue-700 bg-blue-100 border-blue-200";

  return (
    <div className="bg-white border border-gray-100 rounded-xl overflow-hidden" data-testid={`riesgo-${riesgo.id}`}>
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-start gap-3 p-3 text-left hover:bg-gray-50 transition-all">
        <span className="w-5 h-5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{idx + 1}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold border ${color}`}>{tipo}</span>
            <span className="text-[10px] text-gray-400">{riesgo.wi_afectados?.length || 0} WIs afectados</span>
          </div>
          <p className="text-xs font-medium text-gray-900 leading-snug">{riesgo.descripcion}</p>
        </div>
        {open ? <Minimize2 size={12} className="text-gray-400 flex-shrink-0 mt-0.5" /> : <ChevronRight size={12} className="text-gray-400 flex-shrink-0 mt-0.5" />}
      </button>
      {open && (
        <div className="border-t border-gray-100 p-3 bg-gray-50/50 space-y-2">
          <div>
            <p className="text-[10px] font-bold text-red-700 uppercase tracking-wide mb-1">Impacto potencial</p>
            <p className="text-xs text-gray-700">{riesgo.impacto_potencial}</p>
          </div>
          <div>
            <p className="text-[10px] font-bold text-green-700 uppercase tracking-wide mb-1">Mitigación</p>
            <p className="text-xs text-gray-700">{riesgo.mitigacion}</p>
          </div>
          {riesgo.wi_afectados?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {riesgo.wi_afectados.slice(0, 6).map(wi => (
                <span key={wi} className="text-[9px] bg-white border border-gray-200 rounded px-1.5 py-0.5 font-mono text-gray-600">{wi}</span>
              ))}
              {riesgo.wi_afectados.length > 6 && <span className="text-[9px] text-gray-400">+{riesgo.wi_afectados.length - 6} más</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Critical Path Item ───────────────────────────────────────────────────────
function CriticalPathNode({ nodo, wi }) {
  const tipo = wi?.type || nodo.tipo || "Task";
  const typeCls = tipo === "Epic" ? "bg-purple-100 text-purple-800"
    : tipo === "Feature" ? "bg-blue-100 text-blue-800"
    : "bg-gray-100 text-gray-700";

  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-shrink-0 w-2 h-2 rounded-full bg-red-500" />
      <div className="bg-white border border-red-200 rounded-lg px-3 py-1.5 text-xs min-w-0 hover:border-red-400 transition-all" data-testid={`cc-node-${nodo}`}>
        <div className="flex items-center gap-1.5">
          <span className={`text-[9px] px-1.5 rounded font-semibold ${typeCls}`}>{tipo}</span>
          <span className="font-mono text-gray-500 text-[10px]">{nodo}</span>
        </div>
        {wi && <p className="text-gray-800 font-medium truncate max-w-[200px] mt-0.5">{wi.title}</p>}
      </div>
      <div className="flex-shrink-0 w-3 h-px bg-red-300" />
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────
export default function ORAZaragoza() {
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [licitacion, setLicitacion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboardExpanded, setDashboardExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState("overview"); // "overview" | "dashboard" | "riesgos"
  const iframeRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [projRes, licRes] = await Promise.all([
          axios.get(`${API}/projects`),
          axios.get(`${API}/licitaciones`),
        ]);
        const ora = (projRes.data || []).find(p => p.id === "ora-zaragoza-2026");
        setProject(ora);
        const oraLic = (licRes.data || []).find(l => l.municipio === "Zaragoza" && l.tipo_servicio === "Movilidad / ORA");
        setLicitacion(oraLic);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-24">
      <Loader2 className="animate-spin text-[#007934]" size={28} />
    </div>
  );

  const riesgos = project?.riesgos_proyecto || [];
  const cc = project?.camino_critico || {};
  const ccNodos = cc.nodos || [];
  const ccCadena = cc.cadena_texto || "";

  const SECTIONS = [
    { id: "overview", label: "Resumen ejecutivo" },
    { id: "dashboard", label: "Dashboard interactivo" },
    { id: "sprints", label: "Sprints" },
    { id: "hitos", label: "Hitos" },
    { id: "reuniones", label: "Reuniones" },
    { id: "riesgos",   label: `Riesgos (${riesgos.length})` },
  ];

  return (
    <div className="space-y-5 w-full" data-testid="ora-zaragoza-page">

      {/* Header */}
      <div className="flex items-start gap-4">
        <button onClick={() => navigate("/projects")} className="p-2 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 flex-shrink-0">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="bg-emerald-100 text-emerald-800 text-xs px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
              <Trophy size={10} /> GANADA
            </span>
            <span className="text-xs text-gray-400 font-mono">SERZ-ORA-2026-ZGZ</span>
            <span className="text-xs text-gray-400">· UTE Valoriza-INDIGO</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">
            Concesión ORA Zaragoza
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Ordenación y Regulación del Aparcamiento · Inicio previsto 01/06/2026</p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          {licitacion && (
            <button onClick={() => navigate(`/licitaciones/${licitacion.id}`)}
              className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-xl text-xs hover:border-[#007934] hover:text-[#007934] transition-all"
              data-testid="ora-crm-link">
              <ExternalLink size={12} /> Ver en CRM
            </button>
          )}
          <button onClick={() => navigate("/checklists")}
            className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-1.5 rounded-xl text-xs hover:border-[#007934] hover:text-[#007934] transition-all"
            data-testid="ora-checklist-link">
            <ClipboardList size={12} /> Requisitos (865)
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard icon={Cpu} label="Work Items" value="1.408" sub="21 sprints planificados" color="text-blue-600" bg="bg-blue-50" />
        <KpiCard icon={GitBranch} label="Camino crítico" value={`${ccNodos.length} hitos`} sub="273 días duración" color="text-red-600" bg="bg-red-50" />
        <KpiCard icon={Users} label="Perfiles" value="43" sub="departamentos/equipos" color="text-violet-600" bg="bg-violet-50" />
        <KpiCard icon={AlertTriangle} label="Riesgos" value={riesgos.length} sub="identificados" color="text-amber-600" bg="bg-amber-50" />
        <KpiCard icon={ClipboardList} label="Requisitos" value="865" sub="43 secciones" color="text-[#007934]" bg="bg-green-50" />
        <KpiCard icon={Calendar} label="Go-live" value="01/06/26" sub="Fecha firma referencia" color="text-gray-700" bg="bg-gray-50" />
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit max-w-full overflow-x-auto">
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => setActiveSection(s.id)}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap flex-shrink-0 ${activeSection === s.id ? "bg-white shadow-sm text-[#007934]" : "text-gray-500 hover:text-gray-700"}`}
            data-testid={`ora-tab-${s.id}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ── SECTION: Overview ──────────────────────────────────────────────── */}
      {activeSection === "overview" && (
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Critical Path */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Camino Crítico — {ccNodos.length} hitos · 273 días
              </h3>
            </div>
            <div className="p-4">
              {/* Cadena */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-[10px] font-bold text-red-700 uppercase tracking-wider mb-1">Cadena crítica</p>
                <p className="text-xs text-gray-700 font-mono leading-relaxed break-all">{ccCadena}</p>
              </div>
              {/* Nodos */}
              <div className="overflow-x-auto pb-2">
                <div className="flex items-center gap-0 min-w-max">
                  {ccNodos.map((n, i) => {
                    const nodeId = typeof n === "object" ? n.wi_id : n;
                    const nodeTitle = typeof n === "object" ? n.titulo : "";
                    const nodeDays = typeof n === "object" ? n.duracion_dias : null;
                    const nodePerfil = typeof n === "object" ? n.perfil : "";
                    return (
                      <React.Fragment key={nodeId || i}>
                        <div className="flex flex-col items-center gap-1">
                          <div
                            className="bg-red-600 text-white rounded-lg px-2.5 py-2 text-center min-w-[90px] max-w-[120px] cursor-default"
                            data-testid={`cc-${nodeId}`}
                            title={nodeTitle}
                          >
                            <p className="text-[9px] font-mono font-bold">{nodeId}</p>
                            {nodeDays && <p className="text-[8px] opacity-80 mt-0.5">{nodeDays}d · {nodePerfil}</p>}
                          </div>
                        </div>
                        {i < ccNodos.length - 1 && (
                          <div className="w-4 h-px bg-red-400 flex-shrink-0 mb-1" />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Project info panel */}
          <div className="space-y-4">
            {project?.description && (
              <div className="bg-[#E8F5E9] rounded-xl p-4 border-l-4 border-[#007934]">
                <p className="text-xs font-bold text-[#007934] uppercase tracking-wide mb-2">Descripción del proyecto</p>
                <p className="text-xs text-gray-700 leading-relaxed">{project.description}</p>
              </div>
            )}
            <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Datos clave</p>
              {[
                { label: "Empresa", val: "UTE Valoriza-INDIGO" },
                { label: "Municipio", val: "Zaragoza" },
                { label: "Tipo", val: "Movilidad / ORA (Parking)" },
                { label: "Inicio previsto", val: "01/06/2026" },
                { label: "Análisis", val: "25/07/2025" },
                { label: "Duración CP", val: "273 días (18 hitos)" },
              ].map(({ label, val }) => (
                <div key={label} className="flex justify-between gap-2">
                  <span className="text-xs text-gray-400">{label}</span>
                  <span className="text-xs text-gray-800 font-medium text-right">{val}</span>
                </div>
              ))}
            </div>
            <button onClick={() => setActiveSection("dashboard")}
              className="w-full flex items-center justify-center gap-2 bg-[#007934] text-white rounded-xl py-2.5 text-sm font-semibold hover:bg-[#006528] transition-all"
              data-testid="open-dashboard-btn">
              <Maximize2 size={14} /> Abrir dashboard interactivo
            </button>
          </div>
        </div>
      )}

      {/* ── SECTION: Dashboard interactivo ──────────────────────────────────── */}
      {activeSection === "dashboard" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="ora-dashboard-section">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 bg-gray-50">
            <div>
              <h3 className="text-sm font-bold text-gray-900">Dashboard interactivo ORA Zaragoza</h3>
              <p className="text-xs text-gray-400">8 vistas: Ejecutiva · Sprints · Grafo CP · Heatmap · Workbook · Trazabilidad · Perfiles · Departamentos</p>
            </div>
            <div className="flex items-center gap-2">
              <a href="/ora-dashboard.html" target="_blank" rel="noreferrer"
                className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:border-[#007934] hover:text-[#007934] transition-all"
                data-testid="ora-fullscreen-btn">
                <ExternalLink size={12} /> Pantalla completa
              </a>
            </div>
          </div>
          <div className="relative" style={{ height: dashboardExpanded ? "calc(100vh - 200px)" : "700px" }}>
            <iframe
              ref={iframeRef}
              src="/ora-dashboard.html"
              className="w-full h-full border-0"
              title="ORA Zaragoza Dashboard"
              data-testid="ora-dashboard-iframe"
            />
            <button
              onClick={() => setDashboardExpanded(e => !e)}
              className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm border border-gray-200 rounded-lg p-2 shadow-md text-gray-600 hover:text-[#007934] hover:border-[#007934] transition-all"
              title={dashboardExpanded ? "Reducir" : "Expandir"}
            >
              {dashboardExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        </div>
      )}

      {/* ── SECTION: Sprints ──────────────────────────────────────────────── */}
      {activeSection === "sprints" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="ora-sprints-section">
          <ProjectSprintsTab projectId={project?.id || "ora-zaragoza-2026"} />
        </div>
      )}

      {/* ── SECTION: Hitos ────────────────────────────────────────────────── */}
      {activeSection === "hitos" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="ora-hitos-section">
          <ProjectMilestonesTab projectId={project?.id || "ora-zaragoza-2026"} />
        </div>
      )}

      {/* ── SECTION: Reuniones ────────────────────────────────────────────── */}
      {activeSection === "reuniones" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden p-1" data-testid="ora-reuniones-section">
          <ProjectMeetings projectId={project?.id || "ora-zaragoza-2026"} />
        </div>
      )}

      {/* ── SECTION: Riesgos ──────────────────────────────────────────────── */}
      {activeSection === "riesgos" && (
        <div className="space-y-3" data-testid="ora-riesgos-section">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-1">Análisis de riesgos del proyecto</p>
            <p className="text-sm text-amber-900">
              Se han identificado <strong>{riesgos.length} riesgos</strong> en el pipeline de implementación ORA Zaragoza.
              Los cuellos de botella en el camino crítico pueden impactar directamente la fecha de go-live (penalidades contractuales).
            </p>
          </div>
          <div className="space-y-2">
            {riesgos.map((r, i) => <RiesgoCard key={r.id || i} riesgo={r} idx={i} />)}
          </div>
          {/* Advertencias */}
          {project?.advertencias_proyecto?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-3">
                Advertencias del pipeline ({project.advertencias_proyecto.length})
              </h4>
              <div className="space-y-1.5">
                {project.advertencias_proyecto.map((adv, i) => (
                  <div key={i} className="flex items-start gap-2 text-xs text-gray-600">
                    <AlertTriangle size={11} className="text-amber-500 flex-shrink-0 mt-0.5" />
                    <span>{adv}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
